/**
 * Christmas patterns — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const christmasPatternsPack: ToilePack = {
    slug: 'christmas-patterns',
    category: 'season',
    name: 'Christmas patterns',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'holly', label: 'Holly', subject: 'Holly leaves and berry clusters scattered evenly across the frame'},
        {key: 'snowflakes', label: 'Snowflakes', subject: 'Six-pointed snowflakes in three sizes tossed across the frame'},
        {key: 'gingerbread', label: 'Gingerbread', subject: 'Gingerbread men, hearts and stars in rows across the frame'},
    ],
    styles: [
        {key: 'knit', label: 'Nordic knit', style: 'nordic fair-isle knit pattern, pixel-stitch motifs in bands, seamless repeat', palette: 'Red, white, forest green and charcoal', framing: 'The motif repeated in even bands across the whole frame, tileable edges'},
        {key: 'vintage', label: 'Vintage paper', style: 'vintage christmas paper print, halftone dots, slight grain, seamless repeat', palette: 'Faded red, green, cream and gold', framing: 'The motif repeated across the whole frame with even spacing, tileable edges'},
        {key: 'modern', label: 'Modern minimal', style: 'modern minimal surface design, flat simplified shapes, generous spacing, seamless repeat', palette: 'Two colours: forest green on cream, with a red accent', framing: 'Sparse repeat across the whole frame, tileable edges, no borders'},
    ],
    directions: ['a plaid version instead', 'in blue and silver', 'a larger repeat scale'],
    directionHint: 'try: a plaid version instead',
    seo: {
        title: 'Christmas patterns: holly, snowflakes, gingerbread',
        metaDescription: 'Nine Christmas seamless patterns — holly, snowflakes, gingerbread — for fabric, paper or screens, in nordic knit, vintage or modern minimal.',
        h1: 'Christmas seamless patterns: holly, snowflakes, gingerbread',
        tagline: 'Nine patterns, three December motifs, three treatments each — tileable for fabric, paper or screens.',
        intro: 'A December surface pattern scattered evenly: holly leaves and berries, six-pointed snowflakes in three sizes, gingerbread men in rows. Each renders as a nordic fair-isle knit, a vintage halftone print, or a sparse modern minimal repeat, tileable with no visible seam. Ask for a different motif, like a plaid, and the same tileable structure carries it.',
        useCases: [
            'a fabric or quilting design',
            'a printable gift wrap or paper source',
            'a phone or desktop background pattern',
            'a small-batch product packaging design',
        ],
        faq: [
            {q: 'Does the pattern tile without a visible seam?', a: 'Yes — every design is a seamless repeat with tileable edges, ready for fabric, paper or a screen background.'},
            {q: 'Can I get a different motif, like a plaid?', a: 'Yes — the direction field takes any December motif, and the same tileable structure renders it in all three treatments.'},
            {q: "What's the difference between the three treatments?", a: 'Nordic knit uses fair-isle pixel-stitch bands, vintage has a halftone-print grain, and modern minimal is a sparse two-colour repeat.'},
            {q: 'What size does this print or export at?', a: 'The pattern is built to repeat, not a fixed sheet — scale it to whatever the fabric, paper or screen needs.'},
        ],
    },
}
